import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';

export interface LeadCreatedEvent {
  type: 'lead.created';
  leadId: string;
  leadName: string;
  companyName: string | null;
  sourceName: string;
  createdByName: string;
}

const CONFETTI_COLORS = ['#2E47D6', '#E8A317', '#1E7A4C', '#B42318', '#8A5A06', '#6B4FD6'];
const CONFETTI_COUNT = 24;

interface ConfettiPiece {
  left: number;
  delayMs: number;
  durationMs: number;
  color: string;
  size: number;
  spinDeg: number;
}

/** Pure CSS confetti — no canvas, no new dependency (the house has none of
 * confetti-js/canvas-confetti/framer-motion). A fixed piece count generated
 * once per mount via `useMemo` so re-renders (e.g. the dismiss countdown,
 * if one's ever added) don't reshuffle it mid-animation. */
function useConfettiPieces(): ConfettiPiece[] {
  return useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, () => ({
        left: Math.random() * 100,
        delayMs: Math.random() * 250,
        durationMs: 900 + Math.random() * 500,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]!,
        size: 5 + Math.random() * 5,
        spinDeg: 180 + Math.random() * 540,
      })),
    [],
  );
}

/**
 * The "new lead" celebration — a brief, auto-dismissing banner with a
 * confetti burst, triggered by `RealtimeProvider` on a `lead.created` SSE
 * event. Deliberately not routed through `useToast()` (`lib/toast.tsx`):
 * that queue is plain text, stacked top-right, and meant for action
 * feedback ("Saved", "Something went wrong") — this is a one-at-a-time,
 * attention-grabbing moment, closer to `ConfirmDialog` in how much visual
 * weight it carries, just non-blocking.
 */
export function LeadCelebrationToast({ event, onDismiss }: { event: LeadCreatedEvent; onDismiss: () => void }) {
  const confetti = useConfettiPieces();

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-100 flex justify-center">
      <div className="relative" style={{ animation: 'celebration-pop-in 320ms cubic-bezier(0.2, 0.9, 0.3, 1.3)' }}>
        <div className="pointer-events-none absolute inset-x-0 -top-3 h-0 overflow-visible">
          {confetti.map((piece, i) => (
            <span
              key={i}
              className="absolute top-0 rounded-[1px]"
              style={{
                left: `${piece.left}%`,
                width: piece.size,
                height: piece.size * 0.4,
                background: piece.color,
                animation: `confetti-fall ${piece.durationMs}ms ease-in ${piece.delayMs}ms forwards`,
                ['--confetti-spin' as string]: `${piece.spinDeg}deg`,
              }}
            />
          ))}
        </div>

        <div className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-dialog border border-border bg-surface px-4 py-3.5 shadow-dialog">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full text-base" style={{ background: '#FFF4D6' }} aria-hidden="true">
            🎉
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm font-semibold text-ink">New lead: {event.leadName}</span>
            <span className="truncate text-[13px] text-text-secondary">
              {event.createdByName} added {event.companyName ? `${event.companyName} · ` : ''}via {event.sourceName}
            </span>
          </div>
          <Link
            to="/leads/$leadId"
            params={{ leadId: event.leadId }}
            onClick={onDismiss}
            className="flex h-7 shrink-0 items-center rounded-chip border border-border-strong px-2.5 text-xs font-semibold text-ink hover:bg-surface-subtle"
          >
            View
          </Link>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="flex size-7 shrink-0 items-center justify-center rounded-chip text-text-muted hover:bg-surface-subtle"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
